import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Users, Plus, Edit, Trash2, X, Loader2, Shield, Mail, Key, UserCheck,
  UserX, AlertTriangle, History, CheckCircle2, RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

const MODULES = [
  { id: 'dashboard', label: 'Tableau de bord' },
  { id: 'agenda', label: 'Agenda société' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'gestion_clients', label: 'Gestion Clients' },
  { id: 'sites', label: 'Sites & Bâtiments' },
  { id: 'fiches_visite', label: 'Fiches Visite' },
  { id: 'chantiers', label: 'Chantiers' },
  { id: 'sous_traitants', label: 'Sous-traitants' },
  { id: 'devis', label: 'Devis' },
  { id: 'factures', label: 'Factures (ventes)' },
  { id: 'factures_achat', label: "Factures d'achat" },
  { id: 'taches', label: 'Tâches' },
  { id: 'chat', label: 'Discussions / Chat' },
  { id: 'prospections', label: 'Prospections' },
  { id: 'marketing', label: 'Community Manager IA' },
  { id: 'agents_ia', label: 'Agents IA / Orchestrateur' },
  { id: 'administratif', label: 'Administratif' },
  { id: 'users', label: 'Utilisateurs (admin)' },
  { id: 'comptabilite', label: 'Comptabilité (admin)' },
  { id: 'activity_log', label: 'Journal d\'activité (admin)' },
];

const ROLES = [
  { id: 'admin', label: 'Administrateur', color: 'bg-red-100 text-red-700' },
  { id: 'commercial', label: 'Commercial', color: 'bg-blue-100 text-blue-700' },
  { id: 'conducteur', label: 'Conducteur de travaux', color: 'bg-orange-100 text-orange-700' },
  { id: 'comptable', label: 'Comptable', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'user', label: 'Utilisateur', color: 'bg-slate-100 text-slate-700' },
  { id: 'custom', label: 'Personnalisé', color: 'bg-purple-100 text-purple-700' },
];

function emptyForm() {
  const perms = {};
  MODULES.forEach(m => { perms[m.id] = { read: true, write: false, delete: false }; });
  return { email: '', name: '', password: '', role: 'user', is_active: true, permissions: perms };
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/users');
      setUsers(r.data.users || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openCreateModal = () => {
    setEditingUserId(null);
    setForm(emptyForm());
    setError('');
    setModalOpen(true);
  };

  const openEditModal = (u) => {
    setEditingUserId(u.user_id);
    const perms = u.permissions || {};
    const filled = {};
    MODULES.forEach(m => { filled[m.id] = perms[m.id] || { read: false, write: false, delete: false }; });
    setForm({
      email: u.email, name: u.name || '', password: '',
      role: u.role || 'user', is_active: !!u.is_active, permissions: filled
    });
    setError('');
    setModalOpen(true);
  };

  const togglePerm = (moduleId, action) => {
    setForm(f => ({
      ...f,
      permissions: { ...f.permissions, [moduleId]: { ...f.permissions[moduleId], [action]: !f.permissions[moduleId][action] } }
    }));
  };

  const applyRolePreset = (role) => {
    let perms = {};
    if (role === 'admin') {
      MODULES.forEach(m => { perms[m.id] = { read: true, write: true, delete: true }; });
    } else if (role === 'commercial') {
      MODULES.forEach(m => {
        perms[m.id] = {
          read: m.id !== 'factures_achat' && m.id !== 'users' && m.id !== 'activity_log',
          write: ['devis', 'gestion_clients', 'fiches_visite', 'contacts',
                   'agenda', 'prospections', 'taches', 'chat'].includes(m.id),
          delete: false,
        };
      });
    } else if (role === 'conducteur') {
      MODULES.forEach(m => {
        perms[m.id] = {
          read: m.id !== 'factures_achat' && m.id !== 'users' && m.id !== 'activity_log',
          write: ['chantiers', 'fiches_visite', 'sous_traitants',
                   'agenda', 'taches', 'chat'].includes(m.id),
          delete: false,
        };
      });
    } else if (role === 'comptable') {
      MODULES.forEach(m => {
        perms[m.id] = {
          read: m.id !== 'users' && m.id !== 'activity_log',
          write: ['devis', 'administratif', 'factures', 'factures_achat',
                   'agenda', 'taches', 'chat'].includes(m.id),
          delete: ['factures_achat'].includes(m.id),
        };
      });
    } else if (role === 'client') {
      MODULES.forEach(m => {
        // Clients only see their own dashboard + factures (read-only, no achat)
        perms[m.id] = {
          read: ['dashboard', 'factures'].includes(m.id),
          write: false,
          delete: false,
        };
      });
    } else {
      // 'user' générique : lecture + écriture basique, pas de suppression,
      // pas d'accès aux modules admin ni aux factures d'achat.
      MODULES.forEach(m => {
        const isAdminOnly = ['users', 'activity_log'].includes(m.id);
        const readable = !isAdminOnly && m.id !== 'factures_achat';
        perms[m.id] = {
          read: readable,
          write: readable && ['agenda', 'taches', 'chat', 'contacts',
                               'fiches_visite'].includes(m.id),
          delete: false,
        };
      });
    }
    setForm(f => ({ ...f, role, permissions: perms }));
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      if (editingUserId) {
        const payload = {
          name: form.name, role: form.role, is_active: form.is_active, permissions: form.permissions
        };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${editingUserId}`, payload);
      } else {
        await api.post('/admin/users', form);
      }
      setModalOpen(false);
      await loadUsers();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Erreur lors de l\'enregistrement');
    }
    setSaving(false);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${u.email} ?`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      await loadUsers();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Erreur');
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  const handleBackfillModules = async () => {
    if (!window.confirm(
      "Cette action va ajouter les nouveaux modules (Agenda, Prospections…) "
      + "aux permissions des utilisateurs qui ne les ont pas encore. "
      + "Aucun accès existant ne sera retiré. Continuer ?"
    )) return;
    setBackfilling(true);
    try {
      const r = await api.post('/admin/users/backfill-modules');
      const added = r.data?.modules_added || {};
      const keys = Object.keys(added);
      const msg = r.data?.users_updated > 0
        ? `${r.data.users_updated} utilisateur(s) mis à jour · modules ajoutés : ${keys.join(', ') || '—'}`
        : "Tous les utilisateurs ont déjà les nouveaux modules.";
      alert(msg);
      await loadUsers();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur pendant la mise à jour");
    } finally { setBackfilling(false); }
  };

  const roleStyle = (r) => ROLES.find(x => x.id === r)?.color || 'bg-slate-100 text-slate-700';
  const roleLabel = (r) => ROLES.find(x => x.id === r)?.label || r;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            Gestion des utilisateurs
          </h1>
          <p className="text-slate-500 text-sm mt-1">Créer, modifier, supprimer des comptes et gérer leurs permissions.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleBackfillModules}
            disabled={backfilling}
            variant="outline"
            className="border-blue-200 text-blue-700 hover:bg-blue-50"
            data-testid="backfill-modules-btn"
            title="Ajoute les nouveaux modules (Agenda, Prospections…) aux utilisateurs qui n'en disposent pas encore"
          >
            {backfilling
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <RefreshCw className="w-4 h-4 mr-2" />}
            Synchroniser les nouveaux modules
          </Button>
          <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvel utilisateur
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div role="table" data-testid="users-table">
            <div role="row" className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-600 uppercase tracking-wide">
              <div className="col-span-3" role="columnheader">Utilisateur</div>
              <div className="col-span-3" role="columnheader">Email</div>
              <div className="col-span-2" role="columnheader">Rôle</div>
              <div className="col-span-2" role="columnheader">Statut</div>
              <div className="col-span-2 text-right" role="columnheader">Actions</div>
            </div>
            {users.map(u => (
              <div key={u.user_id} role="row" className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 items-center" data-testid={`user-row-${u.user_id}`}>
                <div className="col-span-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-semibold text-sm">
                    {(u.name || u.email).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{u.name || '—'}</p>
                    {u.last_login && <p className="text-xs text-slate-400">Connecté {new Date(u.last_login).toLocaleString('fr-FR')}</p>}
                  </div>
                </div>
                <div className="col-span-3 text-sm text-slate-600">{u.email}</div>
                <div className="col-span-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleStyle(u.role)}`}>{roleLabel(u.role)}</span>
                </div>
                <div className="col-span-2">
                  {u.is_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <CheckCircle2 className="w-3 h-3" /> Actif
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">
                      <UserX className="w-3 h-3" /> Inactif
                    </span>
                  )}
                </div>
                <div className="col-span-2 flex justify-end gap-1">
                  <button onClick={() => navigate(`/admin/activity-log?user_id=${u.user_id}`)}
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Voir activité"
                    data-testid={`user-activity-${u.user_id}`}>
                    <History className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEditModal(u)}
                    className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                    title="Modifier"
                    data-testid={`user-edit-${u.user_id}`}>
                    <Edit className="w-4 h-4" />
                  </button>
                  {u.user_id !== currentUser?.user_id && (
                    <button onClick={() => handleDelete(u)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Supprimer"
                      data-testid={`user-delete-${u.user_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {users.length === 0 && (
              <div className="text-center py-12 text-slate-400 text-sm">Aucun utilisateur enregistré.</div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="user-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">
                {editingUserId ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    disabled={!!editingUserId} placeholder="exemple@renoresolve.com" data-testid="user-form-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom complet *</label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Jean Dupont" data-testid="user-form-name" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {editingUserId ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe * (8 caractères min)'}
                </label>
                <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                  placeholder={editingUserId ? '••••••••' : 'Mot de passe robuste'} data-testid="user-form-password" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Rôle</label>
                <div className="grid grid-cols-3 gap-2">
                  {ROLES.map(r => (
                    <button key={r.id} type="button" onClick={() => applyRolePreset(r.id)}
                      className={`p-2 rounded-lg border-2 text-xs font-medium transition-all ${form.role === r.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                      data-testid={`user-form-role-${r.id}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">Sélectionner un rôle pré-remplit les permissions. Vous pouvez ensuite ajuster pièce par pièce ci-dessous.</p>
              </div>

              <div className="flex items-center gap-2">
                <input id="active-toggle" type="checkbox" checked={form.is_active}
                  onChange={e => setForm({...form, is_active: e.target.checked})}
                  className="w-4 h-4" data-testid="user-form-active" />
                <label htmlFor="active-toggle" className="text-sm text-slate-700">Compte actif (l'utilisateur peut se connecter)</label>
              </div>

              {/* Permissions matrix */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-slate-500" />
                  Permissions par module
                </label>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                    <div className="col-span-6">Module</div>
                    <div className="col-span-2 text-center">Lire</div>
                    <div className="col-span-2 text-center">Écrire</div>
                    <div className="col-span-2 text-center">Supprimer</div>
                  </div>
                  {MODULES.map(m => {
                    const isNew = ['agenda', 'prospections'].includes(m.id);
                    return (
                    <div key={m.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-50 items-center text-sm">
                      <div className="col-span-6 text-slate-700 flex items-center gap-2">
                        {m.label}
                        {isNew && (
                          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase bg-blue-100 text-blue-700 tracking-wide">
                            Nouveau
                          </span>
                        )}
                      </div>
                      {['read', 'write', 'delete'].map(act => (
                        <div key={act} className="col-span-2 text-center">
                          <input type="checkbox"
                            checked={!!form.permissions[m.id]?.[act]}
                            onChange={() => togglePerm(m.id, act)}
                            className="w-4 h-4"
                            data-testid={`perm-${m.id}-${act}`} />
                        </div>
                      ))}
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving || !form.email || !form.name || (!editingUserId && !form.password)}
                className="bg-blue-600 hover:bg-blue-700" data-testid="user-form-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingUserId ? 'Enregistrer' : 'Créer le compte'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
